/* eslint-disable camelcase */
import {FrameTypesEnum} from '../models/api/apiTypes';
import {ApplicationEntryPointTypesEnum} from '../models/api/applicationEntryPointModel';
import {trackEvent, TrackingTypesEnum} from '../services/tracking';
import {SidePanelFrame} from '../store/stateless/sidePanelFrameSelectors';

/*
 * Constants.
 */

export enum SidePanelFrameMouseTrackingValuesEnum {
  MOUSE_ENTER = 'mouse_enter',
  MOUSE_LEAVE = 'mouse_leave',
}

/*
 * Types.
 */

export interface SidePanelTrackingIntegrationPayload {
  integration_id: number;
}

export interface SidePanelTrackingPluginPayload {
  plugin_id: number;
}

export interface SidePanelTrackingApplicationEntryPointPayload {
  entry_point_type: ApplicationEntryPointTypesEnum;
  application_uid?: string;
  endpoint_url: string;
}

export type SidePanelTrackingPayload =
  | SidePanelTrackingIntegrationPayload
  | SidePanelTrackingPluginPayload
  | SidePanelTrackingApplicationEntryPointPayload;

/*
 * Helpers.
 */

export function onTrackIntegrationMouseEvent(
  type: SidePanelFrameMouseTrackingValuesEnum,
  name: string,
  payload: SidePanelTrackingPayload,
) {
  trackEvent(computeTrackingEventFromType(type), name, payload);
}

function computeTrackingEventFromType(type: SidePanelFrameMouseTrackingValuesEnum) {
  if (type === SidePanelFrameMouseTrackingValuesEnum.MOUSE_ENTER) {
    return TrackingTypesEnum.INTEGRATION_MOUSE_ENTER;
  }
  return TrackingTypesEnum.INTEGRATION_MOUSE_LEAVE;
}

export function computeSidePanelTrackingFrameName(frame?: SidePanelFrame) {
  if (!frame) {
    return undefined;
  }

  switch (frame.frameType) {
    case FrameTypesEnum.MANAGER:
      return 'Manager';

    case FrameTypesEnum.CALENDAR:
      return 'Calendar';

    case FrameTypesEnum.START_GUIDE:
      return 'Start Guide';

    case FrameTypesEnum.CUSTOMER_VIEW:
      return 'Customer View';

    case FrameTypesEnum.KEYBOARD_HELP:
      return 'Keyboard Shortcuts';

    default:
      return frame.name;
  }
}

export function computeSidePanelTrackingTrackingFramePayload(
  frame?: SidePanelFrame,
): SidePanelTrackingPayload | undefined {
  if (!frame) {
    return undefined;
  }

  switch (frame.frameType) {
    case FrameTypesEnum.INTEGRATION:
      return {integration_id: frame.id};

    case FrameTypesEnum.PLUGIN:
      return {plugin_id: frame.id};

    case FrameTypesEnum.ENTRY_POINT: {
      // Application is an abstraction layer on top of integration and custom plugins that uses the new UI SDK.
      // They can be distinguished using the type ID.

      // Custom plugin generate the type ID by prefixing the custom plugin's ID with "custom-":
      // https://github.com/frontapp/front/blob/0b4dad99bdb6710bb1d92a7bfce7043b890e4cff/server/resources/application_res.js#L47
      const isCustomPlugin = frame.applicationTypeId.startsWith('custom-');

      if (isCustomPlugin) {
        return {plugin_id: computePluginIdFromCustomPlugin(frame.applicationTypeId)};
      }

      // An application representing an integration uses the same typeId and id as the integration.
      return {integration_id: frame.applicationId};
    }

    case FrameTypesEnum.APPLICATION_ENTRY_POINT: {
      return {
        entry_point_type: frame.type,
        application_uid: frame.applicationUid,
        endpoint_url: frame.action?.endpoint || '',
      };
    }

    default:
      return undefined;
  }
}

export function computePluginIdFromCustomPlugin(applicationTypeId: string) {
  return Number.parseInt(applicationTypeId.replace('custom-', ''), 10);
}
